import { useState } from "react";
import { GripVertical, ArrowUp, ArrowDown } from "lucide-react";

/**
 * Lista horizontal de "pílulas" representando as colunas da agenda (funcionários).
 * Permite arrastar para reordenar OU usar setas para mover.
 *
 * Props:
 *  - items: [{ resourceId, resourceTitle }]
 *  - onChange(newIdsArray): callback com nova ordem (apenas IDs)
 */
export default function AgendaColumnReorder({ items, onChange }) {
  const [dragId, setDragId] = useState(null);

  const move = (fromIdx, toIdx) => {
    if (fromIdx === toIdx) return;
    const arr = items.map((i) => i.resourceId);
    const [m] = arr.splice(fromIdx, 1);
    arr.splice(toIdx, 0, m);
    onChange(arr);
  };

  const handleDragStart = (id) => () => setDragId(id);
  const handleDragOver = (e) => e.preventDefault();
  const handleDrop = (targetId) => (e) => {
    e.preventDefault();
    if (!dragId || dragId === targetId) return;
    const fromIdx = items.findIndex((i) => i.resourceId === dragId);
    const toIdx = items.findIndex((i) => i.resourceId === targetId);
    move(fromIdx, toIdx);
    setDragId(null);
  };

  if (!items || items.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-1.5 mb-2" data-testid="agenda-column-reorder">
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground self-center mr-1">
        Ordem das colunas:
      </div>
      {items.map((it, idx) => (
        <div
          key={it.resourceId}
          draggable
          onDragStart={handleDragStart(it.resourceId)}
          onDragOver={handleDragOver}
          onDrop={handleDrop(it.resourceId)}
          className={`group flex items-center gap-1 px-2 py-1 rounded-full bg-secondary/70 hover:bg-secondary border border-border text-[11px] cursor-grab active:cursor-grabbing select-none transition ${
            dragId === it.resourceId ? "opacity-50" : ""
          }`}
          data-testid={`agenda-column-pill-${it.resourceId}`}
          title="Arraste para reordenar"
        >
          <GripVertical className="h-3 w-3 text-muted-foreground" strokeWidth={1.5} />
          <span className="font-medium">{it.resourceTitle}</span>
          <button
            type="button"
            className="opacity-50 hover:opacity-100 hover:bg-white rounded transition"
            onClick={() => move(idx, Math.max(0, idx - 1))}
            disabled={idx === 0}
            title="Mover para esquerda"
            data-testid={`agenda-col-up-${it.resourceId}`}
          >
            <ArrowUp className="h-3 w-3" strokeWidth={1.8} style={{ transform: "rotate(-90deg)" }} />
          </button>
          <button
            type="button"
            className="opacity-50 hover:opacity-100 hover:bg-white rounded transition"
            onClick={() => move(idx, Math.min(items.length - 1, idx + 1))}
            disabled={idx === items.length - 1}
            title="Mover para direita"
            data-testid={`agenda-col-down-${it.resourceId}`}
          >
            <ArrowDown className="h-3 w-3" strokeWidth={1.8} style={{ transform: "rotate(-90deg)" }} />
          </button>
        </div>
      ))}
    </div>
  );
}
